// Cart prices for the Amai "Upsell+ Volume Discounts" app.
//
// The app never changes Shopify's cart. On the cart page and in the drawer it
// rewrites the prices it is mapped to: it strikes our price element through
// (data-integration-price-original) and inserts copies after it holding the
// discounted price (data-integration-price-created) and the saving
// (data-integration-price-saved). The discount itself is applied at checkout.
//
// This module reads those figures and shows them in the theme's own markup.
// At start-up it adds .amai-cart-prices to <html>: from then on CSS hides the
// app-mapped prices (and the app's copies) for good — when a discount ends the
// app puts back a saved, possibly stale copy of the old price — and shows the
// theme's "now" prices, which this module keeps in step with the cart. Lines /
// the drawer subtotal with a discount also get .is-volume-discounted, which
// shows the struck "was" price and the saving. The cart page's Discount and
// Total rows are rendered by component-cart.js from the volume discount
// reported here.
if (!window.DigitalizModules) window.DigitalizModules = {};
if (!window.DigitalizModules.AmaiCartPrices && window.Digitaliz) {
  const app = window.Digitaliz;

  const ORIGINAL = "data-integration-price-original";
  const CREATED = "data-integration-price-created";
  const SAVED = "data-integration-price-saved";
  const STATE = "is-volume-discounted";

  // Price loader (<html>.is-cart-prices-pending, set before first paint in
  // theme.liquid and again on every cart change). It ends once the app has
  // loaded and nothing it touches has changed for QUIET_MS — the app redraws
  // ~0.2–0.5s after a cart change, right after re-reading /cart.js — or after
  // MAX_MS whatever happens. component-cart.css also reveals prices after 5s.
  const PENDING = "is-cart-prices-pending";
  const QUIET_MS = 600;
  const MAX_MS = 4000;

  // Elements this module (or the summary it triggers) writes to — their
  // mutations must not schedule another update.
  const OWN = [
    "[data-volume-was]",
    "[data-volume-now]",
    "[data-volume-save]",
    "[data-cart-page-subtotal-plain]",
    "[data-cart-page-discount-amount]",
    "[data-cart-page-total]"
  ].join(",");

  // Same decimal convention as the shop's money format (theme.js formats with it).
  const placeholder = ((window.themeMoney && window.themeMoney.moneyFormat) || "").match(/\{\{\s*(\w+)\s*\}\}/);
  const COMMA_DECIMAL = Boolean(placeholder && placeholder[1].includes("comma_separator"));

  // "459.60 NIS", "£1,234.56", "Save 40.40 NIS" → cents, or null.
  function parseMoney(text) {
    const match = (text || "").match(/\d[\d.,'\s]*/);
    if (!match) return null;
    let raw = match[0].replace(/[^\d.,]/g, "");
    raw = COMMA_DECIMAL ? raw.replace(/\./g, "").replace(",", ".") : raw.replace(/,/g, "");
    const value = Number(raw);
    return Number.isFinite(value) ? Math.round(value * 100) : null;
  }

  const close = (a, b) => Math.abs(a - b) <= 1;

  class AmaiCartPrices {
    constructor() {
      this.cart = null;
      this.volume = null;
      this.reportedCart = null;
      this.timer = 0;
      this.observer = null;
      this.resourceObserver = null;
      this.pendingSince = 0;
      this.quietTimer = 0;
      this.maxTimer = 0;
      this.onRefresh = (event) => {
        this.cart = (event.detail && event.detail.cart) || this.cart;
        this.schedule();
        this.touch();
      };
      // Cart requests in flight: the loader can't end while one is running.
      this.requests = 0;
      this.onRequestStart = () => {
        this.requests += 1;
        this.startPending();
      };
      // After a cart change the app always re-reads the cart (XHR /cart.js)
      // before it redraws, so the loader also waits for that read to finish.
      this.awaitingAppRead = false;
      this.requestEndedAt = 0;
      this.onRequestEnd = () => {
        this.requests = Math.max(0, this.requests - 1);
        this.awaitingAppRead = true;
        this.requestEndedAt = performance.now();
        this.touch();
      };
    }

    init() {
      document.documentElement.classList.add("amai-cart-prices");
      app.on(app.events.CART_REFRESH, this.onRefresh);
      app.on(app.events.CART_REQUEST_START, this.onRequestStart);
      app.on(app.events.CART_REQUEST_END, this.onRequestEnd);
      this.observer = new MutationObserver((records) => {
        if (!records.some((record) => !this.isOwnMutation(record))) return;
        this.schedule();
        this.touch();
      });
      // The app re-reads the cart (XHR to /cart.js) just before it redraws.
      if ("PerformanceObserver" in window) {
        this.resourceObserver = new PerformanceObserver((list) => {
          const reads = list.getEntries().filter((entry) => entry.initiatorType === "xmlhttprequest" && entry.name.includes("/cart.js"));
          if (!reads.length) return;
          if (reads.some((entry) => entry.responseEnd >= this.requestEndedAt)) this.awaitingAppRead = false;
          this.touch();
        });
        try {
          this.resourceObserver.observe({ type: "resource" });
        } catch (error) {
          this.resourceObserver = null;
        }
      }
      // Page load: the pending state is already on from theme.liquid.
      if (document.documentElement.classList.contains(PENDING)) this.startPending(0);
      document.querySelectorAll("#CartDrawer, [data-cart-page-form]").forEach((root) => {
        this.observer.observe(root, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: [ORIGINAL]
        });
      });
    }

    destroy() {
      document.documentElement.classList.remove("amai-cart-prices", PENDING);
      document.removeEventListener(app.events.CART_REFRESH, this.onRefresh);
      document.removeEventListener(app.events.CART_REQUEST_START, this.onRequestStart);
      document.removeEventListener(app.events.CART_REQUEST_END, this.onRequestEnd);
      if (this.observer) this.observer.disconnect();
      if (this.resourceObserver) this.resourceObserver.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.quietTimer);
      clearTimeout(this.maxTimer);
    }

    // since: when the wait started (performance.now() timeline; 0 = page load)
    startPending(since = performance.now()) {
      document.documentElement.classList.add(PENDING);
      this.pendingSince = since;
      clearTimeout(this.maxTimer);
      this.maxTimer = setTimeout(() => this.endPending(), Math.max(0, MAX_MS - (performance.now() - since)));
      this.touch();
    }

    // Something changed: restart the quiet countdown (only while pending).
    touch() {
      if (!document.documentElement.classList.contains(PENDING)) return;
      clearTimeout(this.quietTimer);
      this.quietTimer = setTimeout(() => this.trySettle(), QUIET_MS);
    }

    trySettle() {
      const config = window.Spurit && window.Spurit.QuantityBreaks2 && window.Spurit.QuantityBreaks2.config;
      const appLoaded = Boolean(config && config.setting);
      // Also wait for the cart (first CART_REFRESH) so update() has data, for
      // any cart request still running, and for the app's re-read after one.
      const waiting = !appLoaded || !this.cart || this.requests > 0 || this.awaitingAppRead;
      if (waiting && performance.now() - this.pendingSince < MAX_MS) {
        this.touch();
        return;
      }
      this.endPending();
    }

    endPending() {
      clearTimeout(this.quietTimer);
      clearTimeout(this.maxTimer);
      this.update();
      document.documentElement.classList.remove(PENDING);
    }

    isOwnMutation(record) {
      const node = record.target;
      const el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      return Boolean(el && el.closest(OWN));
    }

    // A timer rather than requestAnimationFrame: rAF is paused while the tab
    // is hidden, which would leave the app's prices showing until it returns.
    schedule() {
      if (this.timer) return;
      this.timer = setTimeout(() => {
        this.timer = 0;
        this.update();
      }, 0);
    }

    update() {
      if (!this.cart) return;
      const items = new Map(this.cart.items.map((item) => [item.key, item]));

      document.querySelectorAll("[data-cart-page-line-key]").forEach((line) => {
        const item = items.get(line.getAttribute("data-cart-page-line-key"));
        this.updateLine(line, item, ["[data-cart-page-line-total]", "[data-cart-page-line-unit-price]"]);
      });

      document.querySelectorAll("[data-cart-drawer-line-key]").forEach((line) => {
        const item = items.get(line.getAttribute("data-cart-drawer-line-key"));
        this.updateLine(line, item, [".cart-item__price"]);
      });

      this.updateDrawerSubtotal();
      this.updateSummary();
    }

    // The app's figures for one mapped price inside `scope`:
    // null = not discounted, { pending } = the app hasn't caught up with our
    // last re-render yet (its copies still hold the previous quantity's prices).
    readFigures(scope, selector) {
      const original = scope.querySelector(`${selector}[${ORIGINAL}]`);
      const created = scope.querySelector(`${selector}[${CREATED}]`);
      if (!original || !created) return null;

      const was = parseMoney(original.textContent);
      const now = parseMoney(created.textContent);
      if (was === null || now === null || now < 0 || now >= was) return null;

      const savedEl = scope.querySelector(`${selector}[${SAVED}]`);
      const saved = savedEl ? parseMoney(savedEl.textContent) : null;
      if (saved !== null && !close(was - now, saved)) return { pending: true };

      return { was, now };
    }

    updateLine(line, item, selectors) {
      if (!item) return;

      let figures = null;
      for (const selector of selectors) {
        figures = this.readFigures(line, selector);
        if (figures) break;
      }
      if (figures && figures.pending) return;

      // The app may be mapped to the unit price or the line total — either way
      // its figure must match one of them, or it is still showing old prices.
      if (figures && !close(figures.was, item.final_line_price) && !close(figures.was, item.final_price)) return;

      if (!figures) {
        this.write(line, "[data-volume-now='line']", item.final_line_price);
        this.write(line, "[data-volume-now='unit']", item.final_price);
        line.classList.remove(STATE);
        return;
      }

      const ratio = figures.now / figures.was;
      const lineNow = Math.round(item.final_line_price * ratio);
      const unitNow = Math.round(item.final_price * ratio);

      this.write(line, "[data-volume-was='line']", item.final_line_price);
      this.write(line, "[data-volume-now='line']", lineNow);
      this.write(line, "[data-volume-was='unit']", item.final_price);
      this.write(line, "[data-volume-now='unit']", unitNow);
      this.writeSave(line, "[data-volume-save='line']", item.final_line_price - lineNow);
      line.classList.add(STATE);
    }

    updateDrawerSubtotal() {
      const holder = document.querySelector(".cart-drawer__subtotal");
      if (!holder) return;

      const figures = this.readFigures(holder, "[data-cart-total]");
      if (figures && (figures.pending || !close(figures.was, this.cart.total_price))) return;

      if (!figures) {
        this.write(holder, "[data-volume-now='subtotal']", this.cart.total_price);
        holder.classList.remove(STATE);
        return;
      }

      this.write(holder, "[data-volume-was='subtotal']", figures.was);
      this.write(holder, "[data-volume-now='subtotal']", figures.now);
      this.writeSave(holder, "[data-volume-save='subtotal']", figures.was - figures.now);
      holder.classList.add(STATE);
    }

    updateSummary() {
      const summary = document.querySelector("[data-cart-summary]");
      if (!summary || !app.cart || !app.cart.setVolumeDiscount) return;

      const figures = this.readFigures(summary, "[data-cart-page-subtotal]");
      if (figures && (figures.pending || !close(figures.was, this.cart.items_subtotal_price))) return;

      // Report once per cart, even if the amount is unchanged: component-cart.js
      // holds the summary back after a cart change until it hears from us.
      const volume = figures ? figures.was - figures.now : 0;
      if (volume === this.volume && this.cart === this.reportedCart) return;
      this.volume = volume;
      this.reportedCart = this.cart;
      app.cart.setVolumeDiscount(volume, this.cart);
    }

    // Writes a price only when it changed, so our own writes don't keep the
    // mutation observer (and the app's) busy.
    write(scope, selector, cents, options = {}) {
      const el = scope.querySelector(selector);
      if (!el) return;
      const text = `${options.before || ""}${app.currency(cents)}${options.after || ""}`;
      if (el.textContent !== text) app.setCurrency(el, cents, options);
    }

    writeSave(scope, selector, cents) {
      const template = (window.themeStrings && window.themeStrings.saveAmount) || "[amount]";
      const [before, after = ""] = template.split("[amount]");
      this.write(scope, selector, cents, { before, after });
    }
  }

  const instance = new AmaiCartPrices();
  instance.init();
  window.DigitalizModules.AmaiCartPrices = instance;
}
